import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { DnsTemplate, BUILTIN_TEMPLATES } from '@/types/dashboard/domain/DnsTemplate'
import { z } from 'zod'
import { safeLocalStorage } from '@/composables/useSSR'

const STORAGE_KEY = 'dns-templates'

export const useDnsTemplatesStore = defineStore('dnsTemplates', () => {
  const customTemplates = ref<DnsTemplate[]>([])
  const loaded = ref(false)

  // Load templates from localStorage
  const loadTemplates = () => {
    if (loaded.value) return

    const storage = safeLocalStorage()
    if (!storage) {
      loaded.value = true
      return
    }

    try {
      const stored = storage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        const validated = z.array(DnsTemplate).safeParse(parsed)
        if (validated.success) {
          customTemplates.value = validated.data
        }
      }
    } catch {
      // Invalid data, start fresh
      customTemplates.value = []
    }

    loaded.value = true
  }

  // Save templates to localStorage
  const saveTemplates = () => {
    const storage = safeLocalStorage()
    if (!storage) return

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(customTemplates.value))
    } catch {
      // Storage full or unavailable
    }
  }

  // All templates (builtin + custom)
  const allTemplates = computed(() => [...BUILTIN_TEMPLATES, ...customTemplates.value])

  // Get template by ID
  const getTemplateById = (id: string): DnsTemplate | undefined => {
    return allTemplates.value.find((t) => t.id === id)
  }

  // Add custom template
  const addTemplate = (template: Omit<DnsTemplate, 'id' | 'isBuiltin' | 'createdAt'>) => {
    const newTemplate: DnsTemplate = {
      ...template,
      id: `custom-${Date.now()}`,
      isBuiltin: false,
      createdAt: new Date().toISOString()
    }
    customTemplates.value = [...customTemplates.value, newTemplate]
    saveTemplates()
    return newTemplate
  }

  // Update custom template
  const updateTemplate = (id: string, updates: Partial<Omit<DnsTemplate, 'id' | 'isBuiltin'>>) => {
    const index = customTemplates.value.findIndex((t) => t.id === id)
    if (index === -1) return false

    const existing = customTemplates.value[index]
    if (!existing) return false

    customTemplates.value[index] = {
      ...existing,
      ...updates
    }
    customTemplates.value = [...customTemplates.value]
    saveTemplates()
    return true
  }

  // Delete custom template
  const deleteTemplate = (id: string) => {
    const index = customTemplates.value.findIndex((t) => t.id === id)
    if (index === -1) return false

    customTemplates.value = customTemplates.value.filter((t) => t.id !== id)
    saveTemplates()
    return true
  }

  // Initialize on first access
  loadTemplates()

  return {
    customTemplates,
    allTemplates,
    getTemplateById,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    loadTemplates
  }
})
